import React from "react";
import { Header, Footer, CTA } from "./App";
import { Bi } from "./lang";
import { Helmet } from "react-helmet";
import Seo from "./Seo";

/* FAQ content, bilingual. Visible Q&A switches with the language toggle (<Bi>);
   the FAQPage JSON-LD uses the German text (primary SEO target). */
const FAQ = [
  {
    q: {
      de: "Welche E-Invoicing-Portale bindet JUMIBI an?",
      en: "Which e-invoicing portals does JUMIBI connect to?",
    },
    a: {
      de: "Die JUMIBI AI Business Suite ist live an den nationalen E-Invoicing-Plattformen in Italien (SDI), Frankreich (PPF/PDP, Peppol-basiert) und Serbien (E-Faktura). Weitere Länder lassen sich anbinden, ohne separate Spezialanbieter zu beauftragen.",
      en: "The JUMIBI AI Business Suite is live on the national e-invoicing platforms in Italy (SDI), France (PPF/PDP, Peppol-based) and Serbia (E-Faktura). Further countries can be added without engaging separate specialist vendors.",
    },
  },
  {
    q: {
      de: "Funktioniert das mit meinem bestehenden ERP-System?",
      en: "Does it work with my existing ERP system?",
    },
    a: {
      de: "Ja. Die JUMIBI AI Business Suite bindet an bestehende ERP-Systeme an, statt sie zu ersetzen. Die SAP-Integration entsteht in-house durch eigene SAP-Entwickler; andere ERPs werden über API angebunden. Kein ERP-Wechsel nötig.",
      en: "Yes. The JUMIBI AI Business Suite connects to existing ERP systems instead of replacing them. SAP integration is built in-house by our own SAP developers; other ERPs are connected over API. No ERP migration required.",
    },
  },
  {
    q: {
      de: "Deckt JUMIBI Eingangs- und Ausgangsrechnungen ab?",
      en: "Does JUMIBI cover inbound and outbound invoices?",
    },
    a: {
      de: "Ja. Ausgangsrechnungen werden länderkonform an die jeweiligen E-Invoicing-Portale übermittelt; eingehende Rechnungen verarbeitet JUMIBI AP KI-gestützt und bucht sie direkt ins ERP.",
      en: "Yes. Outbound invoices are transmitted to the relevant e-invoicing portals in a compliant format; inbound invoices are processed by JUMIBI AP with AI and posted straight into the ERP.",
    },
  },
  {
    q: {
      de: "Wie schnell ist ein neues Land startklar?",
      en: "How quickly is a new country ready?",
    },
    a: {
      de: "Pro Land aktivierbar, sobald eine E-Rechnungspflicht greift. Jedes weitere Land nutzt dieselbe Plattform und Prozess-Schicht – ohne neue Spezial-Integration und ohne separaten Anbietervertrag.",
      en: "Activated per country as an e-invoicing mandate lands. Each additional country uses the same platform and process layer — without a new specialist integration or a separate vendor contract.",
    },
  },
];

function faqSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q.de,
      acceptedAnswer: { "@type": "Answer", text: f.a.de },
    })),
  };
}

function Country({ flag, name, portal, children }) {
  return (
    <article className="card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h2 className="h2" style={{ marginTop: 0 }}>
        <span aria-hidden="true" style={{ marginRight: 8 }}>{flag}</span>
        {name} <span className="pill" style={{ marginLeft: 6 }}>{portal}</span>
      </h2>
      <p>{children}</p>
    </article>
  );
}

export default function EInvoicingPage() {
  return (
    <>
      <Seo
        title="E-Invoicing Italien, Frankreich & Serbien – ERP-Anbindung | JUMIBI"
        description="E-Rechnung an die Portale Italien (SDI), Frankreich (PPF/PDP) und Serbien (E-Faktura) – angebunden an bestehende ERP-Systeme, für Eingangs- und Ausgangsrechnungen. Weitere Länder skalierbar."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema())}</script>
      </Helmet>
      <Header />
      <main>
        {/* HERO */}
        <section className="page-hero">
          <div className="container">
            <span className="eyebrow eyebrow--dark">
              <Bi en="JUMIBI E-Invoicing" de="JUMIBI E-Invoicing" />
            </span>
            <h1 className="h1">
              <Bi
                en="E-invoicing for Italy, France & Serbia — connected to your existing ERP"
                de="E-Rechnung für Italien, Frankreich & Serbien — angebunden an Ihr bestehendes ERP"
              />
            </h1>
            <p>
              <Bi
                en="The JUMIBI AI Business Suite connects your existing ERP systems to the national e-invoicing portals — for both inbound and outbound invoices — without replacing your ERP. Live in Italy, France and Serbia today; additional countries scale on the same platform."
                de="Die JUMIBI AI Business Suite verbindet Ihre bestehenden ERP-Systeme mit den nationalen E-Invoicing-Portalen — für Eingangs- und Ausgangsrechnungen — ohne Ihr ERP zu ersetzen. Heute live in Italien, Frankreich und Serbien; weitere Länder skalieren auf derselben Plattform."
              />
            </p>
            <div className="pill-list">
              <span className="pill">Italy · SDI</span>
              <span className="pill">France · PPF/PDP</span>
              <span className="pill">Serbia · E-Faktura</span>
              <span className="pill"><Bi en="ERP-agnostic" de="ERP-unabhängig" /></span>
            </div>
          </div>
        </section>

        {/* COUNTRIES */}
        <section className="section section--ice">
          <div className="container" style={{ maxWidth: 1040, display: "flex", flexDirection: "column", gap: 16 }}>
            <Country flag="🇮🇹" name="Italien" portal="SDI">
              <Bi
                en="Outbound invoices are transmitted to the Italian Sistema di Interscambio (SDI) in the required FatturaPA format and reconciled back into the ERP — no separate SDI intermediary contract needed."
                de="Ausgangsrechnungen werden im geforderten FatturaPA-Format an das italienische Sistema di Interscambio (SDI) übermittelt und im ERP rückverbucht — ohne separaten SDI-Dienstleistervertrag."
              />
            </Country>
            <Country flag="🇫🇷" name="Frankreich" portal="PPF/PDP">
              <Bi
                en="Peppol-based connection to the French model (PPF/PDP) for the rollout of the e-invoicing mandate, integrated directly with your ERP for issuing and receiving compliant invoices."
                de="Peppol-basierte Anbindung an das französische Modell (PPF/PDP) für den Rollout der E-Rechnungspflicht — direkt mit Ihrem ERP integriert, für das Ausstellen und Empfangen konformer Rechnungen."
              />
            </Country>
            <Country flag="🇷🇸" name="Serbien" portal="E-Faktura">
              <Bi
                en="Connection to the Serbian E-Faktura system (SEF) for compliant electronic invoicing, with postings flowing straight into the connected ERP."
                de="Anbindung an das serbische E-Faktura-System (SEF) für konforme elektronische Rechnungsstellung — Buchungen fließen direkt in das angebundene ERP."
              />
            </Country>

            {/* SCALABILITY */}
            <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span className="eyebrow"><Bi en="Scales by country" de="Skaliert pro Land" /></span>
              <h2 className="h2" style={{ marginTop: 0 }}>
                <Bi en="One platform, more countries — without specialist vendors" de="Eine Plattform, mehr Länder — ohne Spezialanbieter" />
              </h2>
              <p>
                <Bi
                  en="Switch on a new country as a mandate lands. Each added country runs on the same JUMIBI AI Business Suite instead of a separate vendor contract and a new specialist integration — so compliance scales with your portfolio."
                  de="Aktivieren Sie ein neues Land, sobald eine Pflicht greift. Jedes weitere Land läuft auf derselben JUMIBI AI Business Suite statt über einen separaten Anbietervertrag und eine neue Spezial-Integration — Compliance skaliert mit Ihrem Portfolio."
                />
              </p>
            </div>

            {/* FAQ */}
            <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <span className="eyebrow"><Bi en="FAQ" de="Häufige Fragen" /></span>
              {FAQ.map((f) => (
                <div key={f.q.de}>
                  <h3 className="h3" style={{ margin: "0 0 4px" }}><Bi en={f.q.en} de={f.q.de} /></h3>
                  <p style={{ margin: 0 }}><Bi en={f.a.en} de={f.a.de} /></p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </>
  );
}
